import { Link } from "@tanstack/react-router";
import { Inbox } from "lucide-react";
import { useOpportunities } from "@/api/queries";
import type { OpportunityFilters, OpportunityRow } from "@/api/types";
import { StatusBadge } from "@/components/feedback/status-badge";
import { OrgLink, StoreLink, UserLink } from "@/components/navigation/entity-links";
import { AdminTable, type Column } from "@/components/tables/admin-table";
import { CopyIdsButton } from "@/components/tables/copy-ids";
import { type ResourceTableProps, tablePaging } from "@/components/tables/types";
import { formatRelative, formatShortDateTime } from "@/lib/dates";
import { OPPORTUNITY_STATUS, SOURCE_LABEL, STAGE_LABEL } from "@/lib/status";

export function OpportunitiesTable({ query, onChange, toolbar, hide = [], filtered }: ResourceTableProps<OpportunityFilters>) {
  const q = useOpportunities(query);
  const columns: Column<OpportunityRow>[] = [
    // {
    //   id: "id",
    //   header: "Opportunity ID",
    //   alwaysVisible: true,
    //   cell: (o) => (
    //     <Link to="/opportunities/$opportunityId" params={{ opportunityId: o.id }} className="font-mono text-[11.5px] hover:underline" title={o.id}>
    //       {shortId(o.id)}
    //     </Link>
    //   ),
    // },
    {
      id: "vehicle",
      header: "Vehicle",
      cell: (o) => (
        <Link to="/opportunities/$opportunityId" params={{ opportunityId: o.id }} className="flex flex-col hover:underline" title={o.id}>
          <span className="truncate">{o.vehicle?.title ?? "Unidentified"}</span>
          <span className="font-mono text-[11px] text-muted-foreground">{o.vehicle?.vin ?? "VIN unknown"}</span>
        </Link>
      ),
    },
    {
      id: "organization",
      header: "Organization",
      sortKey: "organization",
      cell: (o) => <OrgLink org={o.organization} />,
    },
    {
      id: "store",
      header: "Store",
      cell: (o) => <StoreLink store={o.store} />,
    },
    { id: "seller", header: "Seller", cell: (o) => o.seller?.name ?? "—" },
    {
      id: "source",
      header: "Source",
      cell: (o) => SOURCE_LABEL[o.source] ?? o.source,
    },
    {
      id: "stage",
      header: "Stage",
      sortKey: "stage",
      cell: (o) => STAGE_LABEL(o.stage),
    },
    {
      id: "status",
      header: "Status",
      cell: (o) => <StatusBadge config={OPPORTUNITY_STATUS[o.status]} />,
    },
    {
      id: "score",
      header: "Rogue Score",
      sortKey: "score",
      align: "right",
      cell: (o) => o.score ?? "—",
    },
    {
      id: "buyer",
      header: "Assigned buyer",
      cell: (o) => <UserLink user={o.assignedUser} />,
    },
    {
      id: "created",
      header: "Created",
      sortKey: "createdAt",
      cell: (o) => <span className="tabular">{formatShortDateTime(o.createdAt)}</span>,
    },
    {
      id: "updated",
      header: "Updated",
      sortKey: "updatedAt",
      cell: (o) => <span className="text-muted-foreground">{formatRelative(o.updatedAt)}</span>,
    },
  ];
  return (
    <AdminTable
      id="opportunities"
      label="Opportunities"
      resourceName="opportunities"
      columns={columns.filter((c) => !hide.includes(c.id))}
      rows={q.data?.items}
      rowKey={(o) => o.id}
      isPending={q.isPending}
      isFetching={q.isFetching}
      error={q.error}
      onRetry={() => void q.refetch()}
      {...tablePaging(query, q.data?.total, onChange)}
      selectable
      bulkActions={(rows) => <CopyIdsButton ids={rows.map((r) => r.id)} />}
      toolbar={toolbar}
      empty={
        filtered
          ? {
              icon: Inbox,
              title: "No opportunities match these filters",
              description: "Clear a filter or widen the date range.",
            }
          : {
              icon: Inbox,
              title: "No opportunities",
              description: "Leads from connected sources become opportunities.",
            }
      }
    />
  );
}
